<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl">Orders</h1>
    <Button variant="outline" class="" @click="exportToCSV" :disabled="allOrders.length === 0" title="Download CSV">
      <Download class="h-4 w-4" />
      Download
    </Button>
  </div>

  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <OrderControls
      @update:status="onStatusChange"
      @update:date-type="onDateTypeChange"
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="isLoading"
      :columns="paymentPlansColumns"
      :data="paginatedOrders"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-number="pageNumber"
      :page-size="pageSize"
      @update:page="onUpdatePage"
    />
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import type { OrderServiceGetManyRequest } from '@/models/orders';
import type { NewOrder } from '@/models/paymentPlans';
import { LAYUP_API_GET_ITEMS_LIMIT } from '@/services/layupApi';
import { fetchOrders } from '@/services/orders';
import { useOrderStore } from '@/stores/orders';
import { displayCurrency, displayDate, displayDateTime } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { calcPlanValue, getBenefactor, getOutstanding, getTotalPaid } from '@/utils/order.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import { Download } from 'lucide-vue-next';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref } from 'vue';
import type { ModelOrderResponse, ModelOrderResponseStateEnum } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import OrderControls from '../orders-controls/OrdersControls.vue';
import { paymentPlansColumns } from './OrdersTableColumns';

const orderStore = useOrderStore();

const isLoading = ref(false);
const orderParams = ref<OrderServiceGetManyRequest>({
  limit: LAYUP_API_GET_ITEMS_LIMIT,
  state: 'PLACED',
  populate: ['plans', 'plans.payments', 'plans.payments.connectorData', 'plans.benefactor', 'supplier'],
});
const previousParams = ref<OrderServiceGetManyRequest>({ ...orderParams.value });
const allOrders = ref<ModelOrderResponse[]>([]);

const paginatedOrders = computed(() => {
  return allOrders.value.slice((pageNumber.value - 1) * pageSize.value, pageNumber.value * pageSize.value);
});

const totalPages = ref(1);
const totalCount = ref(1);
const pageNumber = ref(1);
const pageSize = ref(10);

onMounted(async () => onUpdateFilter(orderParams.value, true));

const onUpdateFilter = async (params: OrderServiceGetManyRequest, skipCheck: boolean = false) => {
  const isFirstLoad = !orderStore.orders;
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  isLoading.value = true;

  if (isFirstLoad || !skipCheck) {
    allOrders.value = [];
  }

  try {
    const orders = await fetchOrders(params);
    orderStore.setOrders(orders.orders ?? []);
    allOrders.value = orders.orders ?? [];

    totalPages.value = Math.ceil(allOrders.value.length / pageSize.value);
    totalCount.value = allOrders.value.length;
    pageNumber.value = 1;

    addMetric('orders-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('orders-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('orders-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onStatusChange = (value: ModelOrderResponseStateEnum): void => {
  orderParams.value.state = value;
  onUpdateFilter(orderParams.value);
};

const onDateTypeChange = (value: string): void => {
  orderParams.value.stateUpdatedAt = value;
  onUpdateFilter(orderParams.value);
};

const onDateRangeChange = (value: DateRange): void => {
  orderParams.value.from = `${value.start?.year}-${value.start?.month}-${value.start?.day}`;
  orderParams.value.to = `${value.end?.year}-${value.end?.month}-${value.end?.day}`;
  onUpdateFilter(orderParams.value);
};

const onClearFilters = (): void => {
  orderParams.value = { limit: LAYUP_API_GET_ITEMS_LIMIT };
  onUpdateFilter(orderParams.value);
};

const onSearchTermChange = (value: string): void => {
  orderParams.value.search = value;
  onUpdateFilter(orderParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
};

const exportToCSV = () => {
  if (allOrders.value.length === 0) {
    console.warn('No data available for export');
    return;
  }
  const csvContent = generateCSV(allOrders.value);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `payment-plans-${dayjs().format('YYYY-MM-DD')}.csv`);
  link.click();
  URL.revokeObjectURL(url);
};

const generateCSV = (data: ModelOrderResponse[]): string => {
  const headerMap: Partial<Record<keyof NewOrder, string>> = {
    customerName: 'Customer Name',
    name: 'Order Name',
    reference: 'Reference',
    dateCreated: 'Date Created',
    expireDate: 'Expiration Date',
    planValue: 'Plan Value',
    totalReceived: 'Total Received',
    outstanding: 'Outstanding Amount',
    finalPayment: 'Final Payment',
    state: 'Status',
  };

  const exportData: Partial<NewOrder>[] = data.map((order) => {
    const totalPaid = getTotalPaid(order);
    const outstanding = getOutstanding(order);
    const planValue = calcPlanValue(order.plans ? order.plans : []);

    return {
      customerName: getBenefactor(order)?.name ?? 'N/A',
      name: order.name,
      reference: order.reference,
      dateCreated: displayDate(order.createdAt),
      expireDate: displayDateTime(order.expire),
      planValue: displayCurrency(planValue),
      totalReceived: displayCurrency(totalPaid),
      outstanding: displayCurrency(outstanding),
      finalPayment: displayDate(order.endDateMax),
      state: order.state,
    };
  });

  const headers = Object.keys(headerMap) as Array<keyof NewOrder>;
  const headerTitles = headers.map((header) => headerMap[header] || header);
  const rows = exportData.map((row) =>
    headers.map((header) => `"${String(row[header] || '').replace(/"/g, '""')}"`).join(','),
  );

  return [headerTitles.join(','), ...rows].join('\n');
};
</script>
